'use client';

import { forwardRef } from 'react';

import { css, cx, splitResponsiveVariant } from '@/styled-system/css';

import { useDisabledInteractions } from '../../hooks/use-disabled-interactions';
import { Slot } from '../slot';
import { anchorRecipe } from './styles/anchor.styles';
import type { AnchorPropsInterface } from './types/anchor.types';

/**
 * @since 6.0.0
 */

export const Anchor = forwardRef<HTMLAnchorElement, AnchorPropsInterface>(
  (props: AnchorPropsInterface, forwardedRef) => {
    const { onImpact, asChild = false, className, css: cssProp, disabled = false, ...rest } = props;

    const variants = splitResponsiveVariant('onImpact', onImpact);

    const styles = css(anchorRecipe.raw({ ...variants, disabled }), cssProp);

    const Component = asChild ? Slot : 'a';

    // Since anchor elements don't support the `disabled` attribute,
    // we need to override any event interactions
    const disabledInteractionsProps = useDisabledInteractions({ focusable: false });

    return (
      <Component
        ref={forwardedRef}
        data-c11n-component="Anchor"
        data-c11n-version={__C11N_VERSION__}
        className={cx('c11n', 'c11n_anchor', 'c11n-semantic', styles, className)}
        {...rest}
        {...(disabled ? disabledInteractionsProps : {})}
      />
    );
  },
);

Anchor.displayName = 'Anchor';
